// Shared print-template picker for lab results.
//
// Both "Today's Results" (MyPatients.js) and "Lab History" (LabHistory.js)
// open this dialog before launching a print window. The dialog lets the user
// pick between the existing minimal template and the new branded template
// modelled after the Dekyi sample, and — when the branded template is chosen
// — collects the Verified-By / Approved-By signatories that the design
// requires.
//
// We pre-fill the signatory fields from localStorage so users who print
// multiple reports in a session don't have to retype the same names every
// time; the rule from the spec ("every time you have to provide" the
// signatories) is still met because the dialog always opens and the user has
// to actively click Print.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, RadioGroup, FormControlLabel, Radio,
  TextField, Box, Typography, Divider, Stack, Alert,
} from '@mui/material';

import { generateLabReportHTML } from './LabReportTemplate';
import { generateLabReportHTMLDekyi } from './LabReportTemplateDekyi';

const SIG_STORAGE_KEY = 'lab.printSignatories.v1';

function readStoredSignatories() {
  try {
    const raw = localStorage.getItem(SIG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

function persistSignatories(sig) {
  try {
    localStorage.setItem(SIG_STORAGE_KEY, JSON.stringify({
      verifiedBy:    sig.verifiedBy    || '',
      verifiedTitle: sig.verifiedTitle || '',
      approvedBy:    sig.approvedBy    || '',
      approvedTitle: sig.approvedTitle || '',
    }));
  } catch (_) { /* non-fatal */ }
}

export default function LabPrintDialog({
  open,
  onClose,
  reportData,
  selectedPatient,
  userInfo,
  companyInfo,
  categoryInterpretations = {},
  categoryHistograms = {},
  categoryNarratives = {},
  categoryFormats = {},          // category name → 'parameter' | 'narrative'
  referringDoctor = '',
}) {
  // 'branded' is the default because it's the template that renders
  // narrative-marked categories descriptively (the standard template
  // only handles parameter tables). When the report includes any
  // narrative category, we keep this pre-selected so the user just hits
  // Print and gets the right layout.
  const [template, setTemplate] = useState('branded');

  // True when any category in the current report is marked narrative in
  // the catalog. Drives the pre-selection lock + the explainer banner
  // below so the user understands why we're auto-picking Branded.
  // Checked against the row data as well as the category map, because the
  // map only arrives if the calling screen forwarded it — the rows always
  // carry the backend's `lab_format` stamp.
  const hasNarrative =
    Object.values(categoryFormats || {}).some((v) => v === 'narrative') ||
    Object.values(reportData || {}).some((byCategory) =>
      Object.values(byCategory || {}).some((rows) =>
        (rows || []).some((r) => r && r.lab_format === 'narrative')));

  // Whenever the dialog opens for a report that contains a narrative
  // category, snap the template back to Branded — narrative layouts
  // only render through the Dekyi (branded) template path.
  useEffect(() => {
    if (open && hasNarrative) setTemplate('branded');
  }, [open, hasNarrative]);
  const [sig, setSig] = useState({
    verifiedBy: '',
    verifiedTitle: 'Senior Medical Laboratory Scientist',
    approvedBy: '',
    approvedTitle: 'Senior Medical Laboratory Scientist',
    specimenType: 'Blood',
    referringDoctor: '',
  });
  const [touched, setTouched] = useState(false);

  // Pre-fill signatories from the last successful print so users who run
  // batches of reports don't have to retype the same names every time.
  useEffect(() => {
    if (!open) return;
    const stored = readStoredSignatories();
    setSig((prev) => ({
      ...prev,
      verifiedBy:      stored.verifiedBy    || prev.verifiedBy,
      verifiedTitle:   stored.verifiedTitle || prev.verifiedTitle,
      approvedBy:      stored.approvedBy    || prev.approvedBy,
      approvedTitle:   stored.approvedTitle || prev.approvedTitle,
      referringDoctor: referringDoctor || prev.referringDoctor,
    }));
    setTouched(false);
  }, [open, referringDoctor]);

  const handleField = (key) => (e) => setSig((s) => ({ ...s, [key]: e.target.value }));

  const needsSignatories = template === 'branded';
  const missing = needsSignatories && (!sig.verifiedBy.trim() || !sig.approvedBy.trim());

  const handlePrint = () => {
    if (missing) { setTouched(true); return; }

    let html;
    if (template === 'branded') {
      persistSignatories(sig);
      html = generateLabReportHTMLDekyi(reportData, selectedPatient, userInfo, companyInfo, {
        verifiedBy:      sig.verifiedBy.trim(),
        verifiedTitle:   sig.verifiedTitle.trim(),
        approvedBy:      sig.approvedBy.trim(),
        approvedTitle:   sig.approvedTitle.trim(),
        specimenType:    sig.specimenType.trim(),
        referringDoctor: sig.referringDoctor.trim(),
      }, categoryInterpretations, categoryHistograms, categoryNarratives, categoryFormats);
    } else {
      html = generateLabReportHTML(reportData, selectedPatient, userInfo, companyInfo, categoryInterpretations, categoryHistograms, categoryNarratives);
    }

    const w = window.open('', '_blank');
    if (!w) {
      // Popup blocked — surface a friendlier error than a silent failure.
      window.alert('Could not open the print window. Please allow pop-ups for this site and try again.');
      return;
    }
    w.document.write(html);
    w.document.close();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Print Lab Results</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose a report layout. The branded layout matches the printed Dekyi sample
          and requires the verifying and approving signatories before it can be printed.
        </Typography>

        {/* Auto-selection banner — surfaced when one or more categories
            in this report are marked Narrative in Setup Lab. The Branded
            template is the path that renders narrative sections; picking
            Standard would silently drop them, so we lock to Branded and
            tell the user why. */}
        {hasNarrative && (
          <Alert
            severity="info"
            sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
          >
            This report includes a lab marked <b>Narrative</b> in Setup Lab —
            the Branded layout has been pre-selected automatically because it's
            the only one that renders the descriptive section + Impression block.
          </Alert>
        )}

        <RadioGroup
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          sx={{ gap: 0.5 }}
        >
          <Box
            onClick={() => setTemplate('branded')}
            sx={{
              p: 1.25, border: '1.5px solid',
              borderColor: template === 'branded' ? 'primary.main' : 'divider',
              borderRadius: 1.5, cursor: 'pointer',
              bgcolor: template === 'branded' ? 'action.hover' : 'transparent',
            }}
          >
            <FormControlLabel
              value="branded"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    Branded (Dekyi layout)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Blue header band, scannable barcode, three-row signature block.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            onClick={() => { if (!hasNarrative) setTemplate('standard'); }}
            sx={{
              p: 1.25, border: '1.5px solid',
              borderColor: template === 'standard' ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              cursor: hasNarrative ? 'not-allowed' : 'pointer',
              opacity: hasNarrative ? 0.55 : 1,
              bgcolor: template === 'standard' ? 'action.hover' : 'transparent',
            }}
          >
            <FormControlLabel
              value="standard"
              disabled={hasNarrative}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    Standard
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hasNarrative
                      ? "Disabled — this template can't render narrative sections. Use Branded above."
                      : 'The original minimal layout — no barcode or signature rows.'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </RadioGroup>

        {template === 'branded' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>
              Signatories (required)
            </Typography>

            <Stack spacing={1.25}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Verified by — Name"
                  size="small"
                  value={sig.verifiedBy}
                  onChange={handleField('verifiedBy')}
                  error={touched && !sig.verifiedBy.trim()}
                  helperText={touched && !sig.verifiedBy.trim() ? 'Required' : ''}
                  required
                />
                <TextField
                  label="Verified by — Position"
                  size="small"
                  value={sig.verifiedTitle}
                  onChange={handleField('verifiedTitle')}
                  placeholder="e.g., Senior Medical Laboratory Scientist"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Approved by — Name"
                  size="small"
                  value={sig.approvedBy}
                  onChange={handleField('approvedBy')}
                  error={touched && !sig.approvedBy.trim()}
                  helperText={touched && !sig.approvedBy.trim() ? 'Required' : ''}
                  required
                />
                <TextField
                  label="Approved by — Position"
                  size="small"
                  value={sig.approvedTitle}
                  onChange={handleField('approvedTitle')}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Specimen Type"
                  size="small"
                  value={sig.specimenType}
                  onChange={handleField('specimenType')}
                  placeholder="e.g., Blood, Urine"
                />
                <TextField
                  label="Referring Doctor"
                  size="small"
                  value={sig.referringDoctor}
                  onChange={handleField('referringDoctor')}
                  placeholder="Optional"
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Tested by is auto-filled from your logged-in account
                (<strong>{userInfo?.name || userInfo?.username || '—'}</strong>).
              </Typography>

              {touched && missing && (
                <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>
                  Verified-by and Approved-by names are required for the branded layout.
                </Alert>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePrint}>Open Print Preview</Button>
      </DialogActions>
    </Dialog>
  );
}
